import cn from 'classnames';
import React from 'react';
import template from 'lodash.template';
import { useInView } from 'react-intersection-observer';

import useMartyContext from 'hooks/useMartyContext';
import { AriaLiveTee } from 'components/common/AriaLive';
import ProductUtils from 'helpers/ProductUtils';
import { ProductSizing, ProductStockData, ProductStyle } from 'types/cloudCatalog';
import { FormattedProductBundle } from 'reducers/detail/productDetail';
import { SelectedSizing } from 'types/product';
import AddToCartSticky from 'components/productdetail/stylepicker/AddToCartSticky';

import css from 'styles/components/productdetail/addToCart.scss';

interface Props {
  addToCartText: string;
  productDetail?: FormattedProductBundle;
  className?: string;
  hydraCola: boolean;
  useStickyAddToCart: boolean;
  isGiftCard: boolean;
  lowStockMessage: string;
  onAddToCart: (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => void;
  onOpenProductNotifyMe: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onHideSelectSizeTooltip: () => void;
  onShowSelectSizeTooltip: () => void;
  productStyle: ProductStyle;
  selectedSizing: SelectedSizing;
  sizing: ProductSizing;
  form?: string;
}

function formatStockMessage(message: string) {
  const pattern = /^Only (\d+) in stock$/;
  const match = message.match(pattern);
  return match ? `Only ${match[1]} left in stock!` : message;
}

// TODO consider deleting this type declaration at the conclusion of the `cola` hydra test
type ButtonPropsFromParent = Pick<Props, 'form' | 'onHideSelectSizeTooltip' | 'onOpenProductNotifyMe' | 'onShowSelectSizeTooltip'>;

// TODO consider deleting this type declaration at the conclusion of the `cola` hydra test
interface ButtonProps extends ButtonPropsFromParent {
  addToCartRef: (node?: Element | null | undefined) => void;
  buttonText: string;
  isDisabled: boolean | undefined;
  isSelectionValid: boolean;
  onHand: number;
  stock: ProductStockData | null;
  stockId: string | undefined;
}

// TODO consider inlining or deleting this component at the conclusion of the `cola` hydra test
function ColaControlButtonElement(props: ButtonProps) {
  const { addToCartRef, buttonText, form, isDisabled, onHideSelectSizeTooltip, onShowSelectSizeTooltip, stockId } = props;

  const { preventOnTouchDevice, testId } = useMartyContext();

  return (
    <button
      type="submit"
      className={css.cartButton}
      onMouseEnter={preventOnTouchDevice(onShowSelectSizeTooltip)}
      onMouseLeave={preventOnTouchDevice(onHideSelectSizeTooltip)}
      onFocus={preventOnTouchDevice(onShowSelectSizeTooltip)}
      onBlur={preventOnTouchDevice(onHideSelectSizeTooltip)}
      disabled={isDisabled}
      data-stock-id={stockId}
      data-track-action="Product-Page"
      data-track-label="PrForm"
      data-track-value="Add-To-Cart"
      data-test-id={testId('addToCart')}
      ref={addToCartRef}
      form={form}
    >
      {buttonText}
    </button>
  );
}

// TODO consider inlining or deleting this component at the conclusion of the `cola` hydra test
function ColaTreatmentButtonElement(props: ButtonProps) {
  const { addToCartRef, isSelectionValid, onHand, onOpenProductNotifyMe, stock } = props;
  const { testId } = useMartyContext();

  if (isSelectionValid && (!stock || !(onHand > 0))) {
    return (
      <button type="button" onClick={onOpenProductNotifyMe} className={css.cartButton} data-test-id={testId('addToCart')} ref={addToCartRef}>
        Out of Stock/Notify Me
      </button>
    );
  }
  return <ColaControlButtonElement {...props} />;
}

export const AddToCart = (props: Props) => {
  const {
    addToCartText,
    productDetail,
    className,
    hydraCola,
    useStickyAddToCart,
    isGiftCard,
    lowStockMessage,
    onAddToCart,
    onHideSelectSizeTooltip,
    onOpenProductNotifyMe,
    onShowSelectSizeTooltip,
    productStyle,
    productStyle: { colorId, productId },
    selectedSizing,
    sizing,
    form
  } = props;
  const {
    marketplace: {
      pdp: { oosDisableButton }
    },
    testId
  } = useMartyContext();

  let onHand = 1;
  let buttonText = addToCartText;
  let stockMessage: string | undefined;
  let stockInput: JSX.Element | undefined;
  let stock = null;
  let stockId: string | undefined;
  let isDisabled: boolean | undefined;

  // If all dimensions are chosen, check the stock
  const isSelectionValid = ProductUtils.isSizeSelectionComplete(sizing, selectedSizing);
  if (isSelectionValid) {
    stock = ProductUtils.getStockBySize(sizing.stockData, colorId, selectedSizing);
    if (stock) {
      stockInput = <input type="hidden" name="stockId" value={stock.id} />;
      stockId = stock.id;
      onHand = parseInt(stock.onHand);
      if (onHand < 10 && lowStockMessage) {
        const compiled = template(lowStockMessage);
        stockMessage = compiled({ onHand });
      }
    } else {
      isDisabled = oosDisableButton;
      onHand = 0;
      buttonText = 'Out of Stock';
    }
  }

  const [addToCartRef, inView, entry] = useInView({ threshold: 0 });

  const buttonProps: ButtonProps = {
    addToCartRef,
    buttonText,
    form,
    isDisabled,
    isSelectionValid,
    onHand,
    onHideSelectSizeTooltip,
    onOpenProductNotifyMe,
    onShowSelectSizeTooltip,
    stock,
    stockId
  };

  return (
    <div className={cn(css.addToCartControl, { [css.giftCard]: isGiftCard }, className)}>
      {stockInput}
      <input type="hidden" name="productId" value={productId} />
      <input type="hidden" name="colorId" value={colorId} />
      {stockMessage && (
        <div className={css.stockMessage} data-test-id={testId('stockMessage')}>
          <AriaLiveTee>{formatStockMessage(stockMessage)}</AriaLiveTee>
        </div>
      )}
      <div className={css.btnContainer}>
        {hydraCola ? <ColaTreatmentButtonElement {...buttonProps} /> : <ColaControlButtonElement {...buttonProps} />}
      </div>
      {useStickyAddToCart && (
        <AddToCartSticky
          inView={inView}
          entry={entry}
          productDetail={productDetail}
          addToCartText={buttonText}
          isDisabled={onHand < 1}
          stockId={stockId}
          productStyle={productStyle}
          isSelectionValid={isSelectionValid}
          onAddToCart={onAddToCart}
          onShowSelectSizeTooltip={onShowSelectSizeTooltip}
          onHideSelectSizeTooltip={onHideSelectSizeTooltip}
        />
      )}
    </div>
  );
};

export default AddToCart;
